import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import { Check, PencilSimple, X, Plus, Trash } from "@phosphor-icons/react";
import { EmailMarketing } from "@/components/admin/EmailMarketing";
import { PriceLeads } from "@/components/admin/PriceLeads";

const STATUSES = ["pending", "documents_received", "paid", "active", "completed", "rejected"];

function CourseEditor() {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null); // {code, price, name, description, duration, popular}
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = () => api.get("/courses").then(r => setCourses(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (c) => {
    setErr(""); setOk("");
    setEditing({
      code: c.code,
      name: c.name,
      description: c.description,
      duration: c.duration,
      price: c.price,
      price_first: c.price_first ?? c.price,
      price_additional: c.price_additional ?? c.price,
      min_age: c.min_age,
      popular: c.popular
    });
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { code, ...payload } = editing;
      await api.patch(`/admin/courses/${code}`, payload);
      setOk(`${code} güncellendi`);
      setEditing(null);
      await load();
      setTimeout(() => setOk(""), 3000);
    } catch (e) { setErr(e.response?.data?.detail || "Güncelleme hatası"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {ok && <div className="p-3 bg-brand-success/10 border border-brand-success/30 rounded-lg text-brand-success font-semibold flex items-center gap-2"><Check weight="bold"/> {ok}</div>}
      {err && <div className="p-3 bg-brand-error/10 border border-brand-error/30 rounded-lg text-brand-error font-semibold">{err}</div>}
      {courses.map(c => (
        <div key={c.code} className="nb-card p-6" data-testid={`admin-course-${c.code}`}>
          {editing?.code === c.code ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-heading font-bold text-xl">{c.code}</div>
                <button onClick={() => setEditing(null)} className="text-stone-500 hover:text-brand-error p-1" data-testid={`cancel-edit-${c.code}`}><X weight="bold"/></button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><label className="nb-label">Kurs Adı</label><input value={editing.name} onChange={e=>setEditing({...editing, name:e.target.value})} className="nb-input mt-1"/></div>
                <div><label className="nb-label">Genel Fiyat (₺) <span className="text-stone-400 font-normal normal-case">(sitemap/SEO referans)</span></label><input type="number" value={editing.price} onChange={e=>setEditing({...editing, price:parseInt(e.target.value)||0})} className="nb-input mt-1" data-testid={`edit-price-${c.code}`}/></div>
                <div>
                  <label className="nb-label">
                    İlk Ehliyet Fiyatı (₺)
                    <span className="ml-1 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-normal normal-case">Fiyat Danışmanı</span>
                  </label>
                  <input type="number" value={editing.price_first} onChange={e=>setEditing({...editing, price_first:parseInt(e.target.value)||0})} className="nb-input mt-1 !border-green-300 focus:!border-green-500" data-testid={`edit-price-first-${c.code}`}/>
                  <p className="text-[11px] text-stone-400 mt-1">Hiç ehliyeti olmayana gösterilir</p>
                </div>
                <div>
                  <label className="nb-label">
                    Ek Sınıf Fiyatı (₺)
                    <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-normal normal-case">Fiyat Danışmanı</span>
                  </label>
                  <input type="number" value={editing.price_additional} onChange={e=>setEditing({...editing, price_additional:parseInt(e.target.value)||0})} className="nb-input mt-1 !border-blue-300 focus:!border-blue-500" data-testid={`edit-price-additional-${c.code}`}/>
                  <p className="text-[11px] text-stone-400 mt-1">Başka sınıfı olana gösterilir</p>
                </div>
                <div><label className="nb-label">Süre</label><input value={editing.duration} onChange={e=>setEditing({...editing, duration:e.target.value})} className="nb-input mt-1"/></div>
                <div><label className="nb-label">Min. Yaş</label><input type="number" value={editing.min_age} onChange={e=>setEditing({...editing, min_age:parseInt(e.target.value)||0})} className="nb-input mt-1"/></div>
                <div className="md:col-span-2"><label className="nb-label">Açıklama</label><textarea rows={2} value={editing.description} onChange={e=>setEditing({...editing, description:e.target.value})} className="nb-input mt-1"/></div>
                <label className="flex items-center gap-2 text-sm font-medium md:col-span-2">
                  <input type="checkbox" checked={editing.popular} onChange={e=>setEditing({...editing, popular:e.target.checked})} className="w-4 h-4 accent-brand-primary"/>
                  Popüler olarak işaretle
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <button disabled={saving} onClick={save} className="nb-btn" data-testid={`save-${c.code}`}>{saving ? "Kaydediliyor..." : "Kaydet"}</button>
                <button onClick={() => setEditing(null)} className="nb-btn-outline">İptal</button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold text-lg">{c.name}</span>
                  {c.popular && <span className="nb-badge">Popüler</span>}
                </div>
                <div className="text-sm text-stone-500 mt-1">{c.description}</div>
                <div className="mt-2 text-xs text-stone-500">Kod: <strong>{c.code}</strong> · Süre: {c.duration} · Min. yaş: {c.min_age}</div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-2xl text-brand-primary">{c.price.toLocaleString("tr-TR")} ₺</div>
                <div className="text-xs text-stone-400 mt-0.5">
                  İlk: <span className="font-semibold text-green-600">{(c.price_first ?? c.price).toLocaleString("tr-TR")}₺</span>
                  {" · "}Ek: <span className="font-semibold text-blue-600">{(c.price_additional ?? c.price).toLocaleString("tr-TR")}₺</span>
                </div>
                <button onClick={() => startEdit(c)} className="mt-2 nb-btn-outline !px-3 !py-1.5 !text-xs" data-testid={`edit-${c.code}`}><PencilSimple weight="bold" size={14}/> Düzenle</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function UpgradePathsEditor() {
  const [paths, setPaths] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newPath, setNewPath] = useState({ from_class: "", to_class: "", price: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/admin/upgrade-paths"), api.get("/courses")]);
    setPaths(p.data); setCourses(c.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { id, ...upd } = editing;
      await api.patch(`/admin/upgrade-paths/${id}`, upd);
      setOk("Güncellendi"); setEditing(null); await load();
      setTimeout(() => setOk(""), 3000);
    } catch { setErr("Güncelleme hatası"); }
    finally { setSaving(false); }
  };

  const add = async () => {
    if (!newPath.from_class || !newPath.to_class || !newPath.price) { setErr("Tüm alanları doldurun"); return; }
    setSaving(true); setErr("");
    try {
      await api.post("/admin/upgrade-paths", { ...newPath, price: parseInt(newPath.price) });
      setOk("Eklendi"); setAdding(false); setNewPath({ from_class: "", to_class: "", price: "", name: "" });
      await load(); setTimeout(() => setOk(""), 3000);
    } catch { setErr("Ekleme hatası"); }
    finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`"${name}" silinsin mi?`)) return;
    await api.delete(`/admin/upgrade-paths/${id}`);
    await load();
  };

  const courseOptions = courses.map(c => ({ value: c.code, label: c.name }));

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-bold text-2xl">Geçiş Fiyatları</h3>
          <p className="text-sm text-stone-500 mt-0.5">Bir sınıftan diğerine geçiş fiyatları (Fiyat Danışmanı'nda kullanılır)</p>
        </div>
        <button onClick={() => { setAdding(true); setErr(""); }} data-testid="add-upgrade-path-btn"
          className="nb-btn !px-4 !py-2 !text-sm flex items-center gap-1.5">
          <Plus weight="bold" size={14}/> Yeni Geçiş
        </button>
      </div>

      {ok && <div className="mb-3 p-3 bg-brand-success/10 border border-brand-success/30 rounded-lg text-brand-success font-semibold flex items-center gap-2"><Check weight="bold"/> {ok}</div>}
      {err && <div className="mb-3 p-3 bg-brand-error/10 border border-brand-error/30 rounded-lg text-brand-error text-sm">{err}</div>}

      {adding && (
        <div className="mb-4 p-5 border-2 border-brand-primary/30 bg-orange-50 rounded-2xl space-y-3">
          <div className="font-heading font-bold text-base mb-2">Yeni Geçiş Yolu Ekle</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="nb-label">Mevcut Sınıf (Nereden)</label>
              <select value={newPath.from_class} onChange={e=>setNewPath({...newPath,from_class:e.target.value})} className="nb-input mt-1" data-testid="new-path-from">
                <option value="">Seçin</option>
                {courseOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="nb-label">Hedef Sınıf (Nereye)</label>
              <select value={newPath.to_class} onChange={e=>setNewPath({...newPath,to_class:e.target.value})} className="nb-input mt-1" data-testid="new-path-to">
                <option value="">Seçin</option>
                {courseOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="nb-label">Fiyat (₺)</label>
              <input type="number" value={newPath.price} onChange={e=>setNewPath({...newPath,price:e.target.value})} className="nb-input mt-1" placeholder="3500" data-testid="new-path-price"/>
            </div>
            <div>
              <label className="nb-label">Özel İsim <span className="text-stone-400 font-normal">(opsiyonel)</span></label>
              <input value={newPath.name} onChange={e=>setNewPath({...newPath,name:e.target.value})} className="nb-input mt-1" placeholder="A1'den A2 Sınıfı" data-testid="new-path-name"/>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button disabled={saving} onClick={add} className="nb-btn !text-sm" data-testid="save-new-path">{saving ? "Ekleniyor..." : "Ekle"}</button>
            <button onClick={() => { setAdding(false); setErr(""); }} className="nb-btn-outline !text-sm">İptal</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {paths.map(p => (
          <div key={p.id} className="nb-card p-4" data-testid={`upgrade-path-${p.id}`}>
            {editing?.id === p.id ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-stone-500">{p.from_class} → {p.to_class}</div>
                  <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-brand-error"><X weight="bold" size={14}/></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="nb-label">İsim</label>
                    <input value={editing.name} onChange={e=>setEditing({...editing,name:e.target.value})} className="nb-input mt-1" data-testid={`edit-path-name-${p.id}`}/>
                  </div>
                  <div>
                    <label className="nb-label">Fiyat (₺)</label>
                    <input type="number" value={editing.price} onChange={e=>setEditing({...editing,price:parseInt(e.target.value)||0})} className="nb-input mt-1" data-testid={`edit-path-price-${p.id}`}/>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.active} onChange={e=>setEditing({...editing,active:e.target.checked})} className="w-4 h-4 accent-brand-primary"/>
                  Aktif (Fiyat Danışmanı'nda göster)
                </label>
                <div className="flex gap-2">
                  <button disabled={saving} onClick={save} className="nb-btn !text-sm !px-4" data-testid={`save-path-${p.id}`}>{saving ? "..." : "Kaydet"}</button>
                  <button onClick={() => setEditing(null)} className="nb-btn-outline !text-sm !px-4">İptal</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold bg-stone-100 px-2 py-1 rounded">{p.from_class}</span>
                    <span className="text-stone-400 text-sm">→</span>
                    <span className="font-mono text-xs font-bold bg-brand-primary/10 text-brand-primary px-2 py-1 rounded">{p.to_class}</span>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-brand-ink">{p.name}</span>
                    {!p.active && <span className="ml-2 text-[10px] bg-stone-200 text-stone-500 px-2 py-0.5 rounded-full">Pasif</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="font-heading font-bold text-lg text-brand-primary">{p.price?.toLocaleString("tr-TR")} ₺</div>
                  <button onClick={() => setEditing({ id: p.id, name: p.name, price: p.price, active: p.active })}
                    className="nb-btn-outline !px-2.5 !py-1.5 !text-xs" data-testid={`edit-path-btn-${p.id}`}>
                    <PencilSimple weight="bold" size={13}/>
                  </button>
                  <button onClick={() => remove(p.id, p.name)}
                    className="p-1.5 rounded-lg text-stone-400 hover:text-brand-error hover:bg-red-50 transition-colors" data-testid={`delete-path-${p.id}`}>
                    <Trash weight="bold" size={14}/>
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {paths.length === 0 && <p className="text-stone-400 text-sm py-4 text-center">Henüz geçiş yolu eklenmemiş.</p>}
      </div>
    </div>
  );
}


export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("regs");
  const [regs, setRegs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [leadsCount, setLeadsCount] = useState(0);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/registrations").then(r => setRegs(r.data));
    api.get("/admin/contacts").then(r => setMsgs(r.data));
    api.get("/admin/price-leads").then(r => setLeadsCount(r.data.length));
  }, [user]);

  const updateStatus = async (id, v) => {
    const fd = new FormData(); fd.append("status_value", v);
    await api.patch(`/admin/registrations/${id}`, fd);
    setRegs(rs => rs.map(r => r.id === id ? { ...r, status: v } : r));
  };

  const markPaymentReceived = async (id) => {
    await api.patch(`/admin/registrations/${id}/payment-received`);
    setRegs(rs => rs.map(r => r.id === id ? { ...r, status: "paid", payment: { ...(r.payment || {}), status: "received" } } : r));
  };

  if (!user || user.role !== "admin") return <div className="p-12 text-center">Yetkisiz erişim</div>;

  return (
    <>
      <SEO title="Admin Panel | Estem"/>
      <section className="px-4 md:px-8 py-12 max-w-7xl mx-auto">
        <div className="nb-badge mb-2">Admin</div>
        <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">Kontrol Paneli</h1>

        <div className="mt-8 flex gap-2 flex-wrap">
          <button onClick={()=>setTab("regs")} className={`px-5 py-2 border border-stone-300 rounded-full font-semibold text-sm ${tab==="regs"?"bg-brand-ink text-white border-brand-ink":"bg-white"}`} data-testid="admin-tab-regs">Kayıtlar ({regs.length})</button>
          <button onClick={()=>setTab("msgs")} className={`px-5 py-2 border border-stone-300 rounded-full font-semibold text-sm ${tab==="msgs"?"bg-brand-ink text-white border-brand-ink":"bg-white"}`} data-testid="admin-tab-msgs">Mesajlar ({msgs.length})</button>
          <button onClick={()=>setTab("leads")} className={`px-5 py-2 border border-stone-300 rounded-full font-semibold text-sm ${tab==="leads"?"bg-brand-ink text-white border-brand-ink":"bg-white"}`} data-testid="admin-tab-leads">Fiyat Teklifleri ({leadsCount})</button>
          <button onClick={()=>setTab("courses")} className={`px-5 py-2 border border-stone-300 rounded-full font-semibold text-sm ${tab==="courses"?"bg-brand-ink text-white border-brand-ink":"bg-white"}`} data-testid="admin-tab-courses">Kurslar & Fiyatlar</button>
          <button onClick={()=>setTab("faqs")} className={`px-5 py-2 border border-stone-300 rounded-full font-semibold text-sm ${tab==="faqs"?"bg-brand-ink text-white border-brand-ink":"bg-white"}`} data-testid="admin-tab-faqs">SSS</button>
          <button onClick={()=>setTab("blog")} className={`px-5 py-2 border border-stone-300 rounded-full font-semibold text-sm ${tab==="blog"?"bg-brand-ink text-white border-brand-ink":"bg-white"}`} data-testid="admin-tab-blog">Blog</button>
          <button onClick={()=>setTab("email")} className={`px-5 py-2 border border-stone-300 rounded-full font-semibold text-sm ${tab==="email"?"bg-brand-ink text-white border-brand-ink":"bg-white"}`} data-testid="admin-tab-email">E-posta Pazarlama</button>
        </div>

        <div className="mt-6">
          {tab === "regs" && (
            <div className="space-y-3">
              {regs.length === 0 ? <p className="text-stone-500">Henüz kayıt yok.</p> : regs.map(r => (
                <div key={r.id} className="nb-card p-5" data-testid={`admin-reg-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-bold text-lg">{r.full_name} · {r.course_class}</div>
                      <div className="text-sm text-stone-500">TC: {r.tc_no} · Tel: {r.phone} · {r.email}</div>
                      <div className="text-xs text-stone-500 mt-1">{new Date(r.created_at).toLocaleString("tr-TR")}</div>
                      <div className="text-xs mt-1">Belgeler: {(r.documents || []).length}/3 · Plan: {r.payment_method || "—"}</div>
                      {r.payment?.reference_no && (
                        <div className="text-xs mt-1">
                          <span className="text-stone-500">EFT Ref:</span>{" "}
                          <span className="font-mono font-bold text-brand-primary">{r.payment.reference_no}</span>
                          <span className="text-stone-500"> · Tutar: <b>{r.payment.amount_due?.toLocaleString("tr-TR")} ₺</b></span>
                          <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${r.payment.status === "received" ? "bg-brand-success text-white" : "bg-yellow-200"}`}>
                            {r.payment.status === "received" ? "✓ EFT Alındı" : "Ödeme bekleniyor"}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <select value={r.status} onChange={e=>updateStatus(r.id, e.target.value)} className="nb-input !py-2 !w-auto text-sm" data-testid={`admin-status-${r.id}`}>
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {r.payment?.method === "eft" && r.payment?.status !== "received" && (
                        <button onClick={() => markPaymentReceived(r.id)} className="nb-btn !py-1.5 !px-3 !text-xs !bg-brand-success hover:!bg-brand-success" data-testid={`admin-eft-received-${r.id}`}>
                          ✓ EFT Geldi Olarak İşaretle
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "msgs" && (
            <div className="space-y-3">
              {msgs.length === 0 ? <p className="text-stone-500">Mesaj yok.</p> : msgs.map(m => (
                <div key={m.id} className="nb-card p-5">
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <div className="font-semibold">{m.name} <span className="text-stone-500 font-normal">· {m.email} · {m.phone}</span></div>
                      <div className="text-xs text-stone-500 mt-1">{new Date(m.created_at).toLocaleString("tr-TR")}</div>
                      <p className="mt-2">{m.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "courses" && <><CourseEditor/><UpgradePathsEditor/></>}
          {tab === "leads" && <PriceLeads/>}
          {tab === "faqs" && <FAQEditor/>}
          {tab === "blog" && <BlogEditor/>}
          {tab === "email" && <EmailMarketing/>}
        </div>
      </section>
    </>
  );
}

function slugify(text) {
  return text.toString().toLowerCase().trim()
    .replace(/ı/g, "i").replace(/ç/g, "c").replace(/ş/g, "s")
    .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ö/g, "o")
    .replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
}

const BLOG_CATEGORIES = [
  "Ehliyet Rehberi",
  "Sınav Hazırlığı",
  "Trafik Kuralları",
  "Motosiklet",
  "Gebze & Kocaeli",
  "Kurumsal Haberler",
  "Öğrenci Deneyimleri",
];

function BlogEditor() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [slugManual, setSlugManual] = useState(false);

  const load = () => api.get("/admin/blog").then(r => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const flash = (msg) => { setOk(msg); setTimeout(() => setOk(""), 2500); };

  const startNew = () => {
    setErr(""); setSlugManual(false); setCreating(true);
    setEditing({ title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: [], published: false, category: "Ehliyet Rehberi", meta_description: "", meta_keywords: "" });
  };

  const startEdit = async (p) => {
    setErr(""); setCreating(false); setSlugManual(true);
    try {
      const { data } = await api.get(`/admin/blog/id/${p.id}`);
      setEditing({ category: "Ehliyet Rehberi", meta_description: "", meta_keywords: "", ...data });
    } catch (e) { setErr("Yazı yüklenemedi"); }
  };

  const handleTitleChange = (v) => {
    const next = { ...editing, title: v };
    if (creating && !slugManual) next.slug = slugify(v);
    setEditing(next);
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/blog/upload-cover", fd, { headers: {"Content-Type":"multipart/form-data"} });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setEditing({ ...editing, cover_image: `${backendUrl}${data.url}` });
      flash("Kapak yüklendi");
    } catch (e) { setErr(e.response?.data?.detail || "Yükleme hatası"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!editing.title || !editing.slug || !editing.excerpt || !editing.content) {
      setErr("Başlık, slug, özet ve içerik zorunlu"); return;
    }
    setSaving(true); setErr("");
    try {
      const payload = { ...editing, tags: Array.isArray(editing.tags) ? editing.tags : (editing.tags || "").toString().split(",").map(t=>t.trim()).filter(Boolean) };
      if (creating) { await api.post("/admin/blog", payload); flash("Yeni yazı oluşturuldu"); }
      else { await api.patch(`/admin/blog/${editing.id}`, payload); flash("Yazı güncellendi"); }
      setEditing(null); setCreating(false);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "Kaydetme hatası"); }
    finally { setSaving(false); }
  };

  const togglePublish = async (p) => {
    try { await api.patch(`/admin/blog/${p.id}`, { published: !p.published }); await load(); flash(p.published ? "Yayından kaldırıldı" : "Yayınlandı"); }
    catch { setErr("Güncelleme hatası"); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Bu blog yazısını silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/admin/blog/${pid}`); await load(); flash("Silindi"); }
    catch { setErr("Silme hatası"); }
  };

  return (
    <div className="space-y-4">
      {ok && <div className="p-3 bg-brand-success/10 border border-brand-success/30 rounded-lg text-brand-success font-semibold flex items-center gap-2"><Check weight="bold"/> {ok}</div>}
      {err && <div className="p-3 bg-brand-error/10 border border-brand-error/30 rounded-lg text-brand-error font-semibold">{err}</div>}

      {!editing && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-stone-500">{posts.length} yazı ({posts.filter(p=>p.published).length} yayında)</div>
          <button onClick={startNew} className="nb-btn" data-testid="blog-add-new"><Plus weight="bold"/> Yeni Blog Yazısı</button>
        </div>
      )}

      {editing && (
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-8 space-y-5">
            <div className="nb-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="font-heading font-bold text-lg">{creating ? "Yeni Blog Yazısı" : "Yazıyı Düzenle"}</div>
                <button onClick={() => { setEditing(null); setCreating(false); }} className="text-stone-500 hover:text-brand-error p-1"><X weight="bold"/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="nb-label">Başlık *</label>
                  <input value={editing.title} onChange={e=>handleTitleChange(e.target.value)} className="nb-input mt-1" data-testid="blog-title" placeholder="Ör: Gebze'de Ehliyet Almanın Püf Noktaları"/>
                </div>
                <div>
                  <label className="nb-label">Slug (URL)</label>
                  <input value={editing.slug} onChange={e=>{setSlugManual(true); setEditing({...editing, slug:e.target.value});}} className="nb-input mt-1 font-mono text-sm" data-testid="blog-slug" placeholder="ehliyet-almanin-puf-noktalari"/>
                  <div className="text-xs text-stone-500 mt-1">URL: /blog/<strong>{editing.slug || "…"}</strong></div>
                </div>
                <div>
                  <label className="nb-label">İçerik * (HTML supported)</label>
                  <textarea rows={18} value={editing.content} onChange={e=>setEditing({...editing, content:e.target.value})} className="nb-input mt-1 font-mono text-sm" data-testid="blog-content" placeholder="<h2>Alt başlık</h2>&#10;<p>Paragraf metni...</p>"/>
                  <div className="mt-2 p-3 bg-stone-50 border border-stone-200 rounded-lg text-xs text-stone-600 leading-relaxed">
                    <strong className="text-brand-ink">İpucu:</strong> Alt başlıklar için <code className="px-1 bg-white rounded">{"<h2>"}</code>, paragraflar için <code className="px-1 bg-white rounded">{"<p>"}</code>, listeler için <code className="px-1 bg-white rounded">{"<ul><li>"}</code>, kalın metin için <code className="px-1 bg-white rounded">{"<strong>"}</code>, linkler için <code className="px-1 bg-white rounded">{'<a href="…">'}</code> kullanın.
                  </div>
                  <div className="text-xs text-stone-500 mt-1">{editing.content.length} karakter</div>
                </div>
                <div>
                  <label className="nb-label">Özet</label>
                  <textarea rows={2} value={editing.excerpt} onChange={e=>setEditing({...editing, excerpt:e.target.value})} className="nb-input mt-1" data-testid="blog-excerpt" placeholder="Kısa özet (blog kartında görünür)"/>
                </div>
              </div>
            </div>

            {/* SEO Settings */}
            <div className="nb-card p-6">
              <div className="font-heading font-bold text-base mb-4">SEO Ayarları</div>
              <div className="space-y-4">
                <div>
                  <label className="nb-label">Meta Açıklama</label>
                  <textarea rows={2} value={editing.meta_description} onChange={e=>setEditing({...editing, meta_description:e.target.value.slice(0,160)})} className="nb-input mt-1" data-testid="blog-meta-desc" placeholder="Google arama sonuçlarında görünecek açıklama"/>
                  <div className="text-xs text-stone-500 mt-1"><span className={editing.meta_description.length > 155 ? "text-brand-error font-semibold" : ""}>{editing.meta_description.length}</span> / 160</div>
                </div>
                <div>
                  <label className="nb-label">Meta Anahtar Kelimeler</label>
                  <input value={editing.meta_keywords} onChange={e=>setEditing({...editing, meta_keywords:e.target.value})} className="nb-input mt-1" data-testid="blog-meta-keywords" placeholder="gebze sürücü kursu, ehliyet, B sınıfı"/>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-5">
            {/* Post Settings */}
            <div className="nb-card p-6">
              <div className="font-heading font-bold text-base mb-4">Yazı Ayarları</div>
              <div className="space-y-4">
                <div>
                  <label className="nb-label">Durum</label>
                  <div className="mt-2 flex gap-1 p-1 bg-stone-100 rounded-lg">
                    <button onClick={()=>setEditing({...editing, published:false})} className={`flex-1 py-1.5 rounded-md text-sm font-semibold transition-colors ${!editing.published ? "bg-white text-brand-ink shadow-sm" : "text-stone-500"}`} data-testid="blog-status-draft">Taslak</button>
                    <button onClick={()=>setEditing({...editing, published:true})} className={`flex-1 py-1.5 rounded-md text-sm font-semibold transition-colors ${editing.published ? "bg-brand-primary text-white shadow-sm" : "text-stone-500"}`} data-testid="blog-status-published">Yayında</button>
                  </div>
                </div>
                <div>
                  <label className="nb-label">Kategori</label>
                  <select value={editing.category} onChange={e=>setEditing({...editing, category:e.target.value})} className="nb-input mt-1" data-testid="blog-category">
                    {BLOG_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="nb-label">Etiketler</label>
                  <input value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags} onChange={e=>setEditing({...editing, tags:e.target.value.split(",").map(t=>t.trim()).filter(Boolean)})} className="nb-input mt-1" data-testid="blog-tags" placeholder="B Sınıfı, 2026, Rehber"/>
                  <div className="text-xs text-stone-500 mt-1">Virgülle ayırın</div>
                </div>
              </div>
            </div>

            {/* Featured Image */}
            <div className="nb-card p-6">
              <div className="font-heading font-bold text-base mb-4">Öne Çıkan Görsel</div>
              {editing.cover_image && (
                <img src={editing.cover_image} alt="cover" className="w-full h-40 object-cover rounded-lg border border-stone-200 mb-3"/>
              )}
              <div className="space-y-2">
                <input value={editing.cover_image} onChange={e=>setEditing({...editing, cover_image:e.target.value})} className="nb-input" data-testid="blog-cover-url" placeholder="Görsel URL'si yapıştır"/>
                <div className="text-xs text-center text-stone-400">veya</div>
                <label className="nb-btn-outline w-full cursor-pointer !py-2 !text-xs">
                  {uploading ? "Yükleniyor…" : "Bilgisayardan Yükle"}
                  <input type="file" accept="image/*" className="hidden" onChange={e=>uploadCover(e.target.files[0])} data-testid="blog-cover-upload"/>
                </label>
                {editing.cover_image && (
                  <button onClick={()=>setEditing({...editing, cover_image:""})} className="text-brand-error text-xs font-semibold w-full">Kaldır</button>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="nb-card p-6 space-y-2">
              <button disabled={saving} onClick={save} className="nb-btn w-full" data-testid="blog-save">{saving ? "Kaydediliyor..." : (editing.published ? (creating ? "Yayınla" : "Güncelle & Yayınla") : "Taslak Olarak Kaydet")}</button>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="nb-btn-outline w-full">İptal</button>
            </div>
          </div>
        </div>
      )}

      {!editing && (
        <div className="space-y-3">
          {posts.length === 0 ? (
            <div className="nb-card p-12 text-center text-stone-500">Henüz blog yazısı yok. Yeni bir yazı ekleyin.</div>
          ) : posts.map(p => (
            <div key={p.id} className="nb-card p-5" data-testid={`admin-blog-${p.id}`}>
              <div className="flex items-start justify-between gap-4">
                {p.cover_image && <img src={p.cover_image} alt="" className="w-20 h-20 object-cover rounded-lg border border-stone-200 shrink-0"/>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-bold text-base truncate">{p.title}</div>
                    {p.published ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-success/10 text-brand-success uppercase tracking-wider">Yayında</span>
                    ) : (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-stone-200 text-stone-600 uppercase tracking-wider">Taslak</span>
                    )}
                    {p.category && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-primarySubtle text-brand-primary uppercase tracking-wider">{p.category}</span>}
                  </div>
                  <div className="text-xs text-stone-500 mt-1 font-mono">/blog/{p.slug}</div>
                  <div className="text-sm text-stone-600 mt-2 line-clamp-2">{p.excerpt}</div>
                  {p.tags && p.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">{p.tags.map(t => <span key={t} className="text-[10px] px-2 py-0.5 bg-stone-100 text-stone-700 rounded-full font-semibold">{t}</span>)}</div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => startEdit(p)} className="nb-btn-outline !px-3 !py-1.5 !text-xs" data-testid={`blog-edit-${p.id}`}><PencilSimple weight="bold" size={14}/> Düzenle</button>
                  <button onClick={() => togglePublish(p)} className={`!px-3 !py-1.5 !text-xs font-semibold rounded-lg border ${p.published ? "border-stone-300 text-stone-700 hover:bg-stone-100" : "border-brand-success/30 text-brand-success hover:bg-brand-success/10"}`} data-testid={`blog-toggle-${p.id}`}>{p.published ? "Yayından Kaldır" : "Yayınla"}</button>
                  <button onClick={() => remove(p.id)} className="text-brand-error border border-brand-error/30 hover:bg-brand-error/10 rounded-lg !px-3 !py-1.5 !text-xs font-semibold flex items-center justify-center gap-1" data-testid={`blog-delete-${p.id}`}><Trash weight="bold" size={14}/> Sil</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FAQEditor() {
  const [faqs, setFaqs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = () => api.get("/admin/faqs").then(r => setFaqs(r.data));
  useEffect(() => { load(); }, []);

  const flash = (msg) => { setOk(msg); setTimeout(() => setOk(""), 2500); };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      if (creating) {
        await api.post("/admin/faqs", { q: editing.q, a: editing.a, order: editing.order || 0 });
        flash("Yeni SSS eklendi");
      } else {
        await api.patch(`/admin/faqs/${editing.id}`, { q: editing.q, a: editing.a, order: editing.order });
        flash("SSS güncellendi");
      }
      setEditing(null); setCreating(false);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "Kaydetme hatası"); }
    finally { setSaving(false); }
  };

  const remove = async (fid) => {
    if (!window.confirm("Bu SSS'i silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/admin/faqs/${fid}`); flash("Silindi"); await load(); }
    catch (e) { setErr(e.response?.data?.detail || "Silme hatası"); }
  };

  const startCreate = () => { setErr(""); setCreating(true); setEditing({ q: "", a: "", order: faqs.length }); };

  return (
    <div className="space-y-4" data-testid="faq-editor">
      {/* Info banner */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
        <div className="text-amber-500 mt-0.5 shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm1 15h-2v-6h2Zm0-8h-2V7h2Z"/></svg>
        </div>
        <div>
          <div className="font-semibold text-amber-800 text-sm">Bu SSS'ler nerede görünür?</div>
          <div className="text-amber-700 text-xs mt-0.5 leading-relaxed">
            Buraya eklediğiniz her soru-cevap hem <strong>/sss</strong> sayfasında hem de{" "}
            <strong>tüm 29 lokal sayfada</strong> (gebze-surucu-kursu, darica-ehliyet-kursu vb.) otomatik olarak yayınlanır.
            Sıra numarasına göre sıralanır — küçük numara önce gelir.
          </div>
        </div>
      </div>

      {ok && <div className="p-3 bg-brand-success/10 border border-brand-success/30 rounded-lg text-brand-success font-semibold flex items-center gap-2"><Check weight="bold"/> {ok}</div>}
      {err && <div className="p-3 bg-brand-error/10 border border-brand-error/30 rounded-lg text-brand-error font-semibold">{err}</div>}

      {!creating && !editing && (
        <button onClick={startCreate} className="nb-btn" data-testid="faq-add-new"><Plus weight="bold"/> Yeni SSS Ekle</button>
      )}

      {editing && (
        <div className="nb-card p-6 border-2 border-brand-primary/30">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-bold text-lg">{creating ? "Yeni SSS" : "SSS Düzenle"}</div>
            <button onClick={() => { setEditing(null); setCreating(false); }} className="text-stone-500 hover:text-brand-error p-1"><X weight="bold"/></button>
          </div>
          <div className="space-y-3">
            <div><label className="nb-label">Soru</label><input value={editing.q} onChange={e=>setEditing({...editing, q:e.target.value})} className="nb-input mt-1" data-testid="faq-edit-q"/></div>
            <div><label className="nb-label">Cevap</label><textarea rows={4} value={editing.a} onChange={e=>setEditing({...editing, a:e.target.value})} className="nb-input mt-1" data-testid="faq-edit-a"/></div>
            <div><label className="nb-label">Sıra</label><input type="number" value={editing.order} onChange={e=>setEditing({...editing, order:parseInt(e.target.value)||0})} className="nb-input mt-1 !max-w-[120px]"/></div>
            <div className="flex gap-2 pt-2">
              <button disabled={saving} onClick={save} className="nb-btn" data-testid="faq-save">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="nb-btn-outline">İptal</button>
            </div>
          </div>
        </div>
      )}

      {!editing && faqs.map(f => (
        <div key={f.id} className="nb-card p-5" data-testid={`admin-faq-${f.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-400 font-mono">#{f.order}</span>
                <div className="font-heading font-semibold text-base">{f.q}</div>
              </div>
              <div className="text-sm text-stone-600 mt-2 leading-relaxed">{f.a}</div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={() => { setErr(""); setEditing({ ...f }); }} className="nb-btn-outline !px-3 !py-1.5 !text-xs" data-testid={`faq-edit-${f.id}`}><PencilSimple weight="bold" size={14}/> Düzenle</button>
              <button onClick={() => remove(f.id)} className="text-brand-error border border-brand-error/30 hover:bg-brand-error/10 rounded-lg !px-3 !py-1.5 !text-xs font-semibold flex items-center justify-center gap-1" data-testid={`faq-delete-${f.id}`}><Trash weight="bold" size={14}/> Sil</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
